import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ALL_CARRIER_SCACS, CARRIERS } from "@/constants/carriers";

/**
 * A port as the seaport autocomplete returns it, kept whole.
 *
 * `codeFrom`/`codeTo` are persisted, and what is persisted is what gets sent
 * upstream: so the record keeps the UN/LOCODE, the country and the
 * coordinates, not just the label that happens to be on screen.
 */
export interface SeaportRecord {
	/** UN/LOCODE. This is what the schedules and tracking endpoints take. */
	code: string;
	name: string;
	country_code?: string;
	country_name?: string;
	country_flag?: string;
	country_cca3?: string;
	region?: string;
	subregion?: string;
	/** GeoJSON: `[lng, lat]`, the opposite order to `tradeport.location`. */
	location?: { type: "Point"; coordinates: [number, number] };
	score?: number;
}

// Interface for location data from geocoding API
export interface Location {
	name?: string;
	/** The untouched autocomplete record, when this came from the port search. */
	seaport?: SeaportRecord;
	country?: {
		id?: string;
		iso3?: string;
		name: string;
		score?: number;
	};
	flag?: string;
	display_name?: string;
	lat?: number;
	lon?: number;
	tradeport?: {
		name: string;
		location: [number, number];
		locode: string;
		iata?: string;
		score: number;
		features?: {
			is_airport: boolean;
			is_seaport: boolean;
			is_rail_terminal: boolean;
			is_road_terminal: boolean;
			is_icd: boolean;
			is_fixed_transport: boolean;
			is_riverport: boolean | null;
		};
	};
	city?: {
		name: string;
		location: [number, number];
		postcode?: string[];
		score?: number;
	};
	admin?: {
		name: string;
		location: [number, number] | null;
		score?: number;
	};
	worldRegion?: Array<{
		id: string;
		name: string;
		level: number;
	}>;
}

// Container type option
export interface ContainerTypeOption {
	value: string;
	label: string;
}

// Carrier interface: the list is now a hardcoded constant
// (`src/constants/carriers.ts`); there is NO carrier API call anymore. The set
// of major sealines is stable. `scac_codes` (may hold multiple SCACs) is the
// source of truth; use scac_codes[0] as the canonical code.
export interface Carrier {
	id: number | string;
	name: string;
	scac_codes: string[];
	image_url?: string | null;
}

// State interface
export interface GeocodeFiltersState {
	// Location filters
	codeFrom: Location | null;
	codeTo: Location | null;

	// Transport and container filters
	shippingLine: string;
	containerType: ContainerTypeOption | null;
	shippingType: string;
	transportType: string;
	cargoType: string;

	// Date filter (stored as ISO string for serialization)
	selectedDate: string | null;

	// Week ahead filters
	selectedWeeks: number;
	selectedShippingLines: string[];
	allCarrierScacs: string[]; // Canonical SCAC codes when "All sealines" is selected
	carriers: Carrier[]; // Full carrier data with names (seeded from the constant)
	searchOn: string;
	onlyDirectService: boolean;
	showAlternativeOptions: boolean;
}

// Initial state
const initialState: GeocodeFiltersState = {
	// Location filters
	codeFrom: null,
	codeTo: null,

	// Transport and container filters
	shippingLine: "MAEU", // Default shipping line
	containerType: { value: "st20", label: "20' Standard" }, // Default container type
	shippingType: "sea",
	transportType: "containerVessel",
	cargoType: "GC", // Default cargo type for ship schedules

	// Date filter
	selectedDate: new Date().toISOString(),

	// Week ahead filters
	selectedWeeks: 2,
	selectedShippingLines: ["All sealines"],
	allCarrierScacs: ALL_CARRIER_SCACS, // pre-seeded so "All sealines" works immediately
	carriers: CARRIERS,
	searchOn: "departure",
	onlyDirectService: false,
	showAlternativeOptions: false,
};

// Create slice
export const geocodeFiltersSlice = createSlice({
	name: "geocodeFilters",
	initialState,
	reducers: {
		// Location actions
		setCodeFrom: (state, action: PayloadAction<Location | null>) => {
			state.codeFrom = action.payload;
		},
		setCodeTo: (state, action: PayloadAction<Location | null>) => {
			state.codeTo = action.payload;
		},

		// Transport and container actions
		setShippingLine: (state, action: PayloadAction<string>) => {
			state.shippingLine = action.payload;
		},
		setContainerType: (
			state,
			action: PayloadAction<ContainerTypeOption | null>,
		) => {
			state.containerType = action.payload;
		},
		setShippingType: (state, action: PayloadAction<string>) => {
			state.shippingType = action.payload;
		},
		setTransportType: (state, action: PayloadAction<string>) => {
			state.transportType = action.payload;
		},
		setCargoType: (state, action: PayloadAction<string>) => {
			state.cargoType = action.payload;
		},

		// Date actions
		setSelectedDate: (state, action: PayloadAction<string | null>) => {
			state.selectedDate = action.payload;
		},

		// Week ahead actions
		setSelectedWeeks: (state, action: PayloadAction<number>) => {
			state.selectedWeeks = action.payload;
		},
		setSelectedShippingLines: (state, action: PayloadAction<string[]>) => {
			state.selectedShippingLines = action.payload;
		},
		setAllCarrierScacs: (state, action: PayloadAction<string[]>) => {
			state.allCarrierScacs = action.payload;
		},
		setSearchOn: (state, action: PayloadAction<string>) => {
			state.searchOn = action.payload;
		},
		setOnlyDirectService: (state, action: PayloadAction<boolean>) => {
			state.onlyDirectService = action.payload;
		},
		setShowAlternativeOptions: (state, action: PayloadAction<boolean>) => {
			state.showAlternativeOptions = action.payload;
		},

		// Reset action
		resetFilters: (state) => {
			Object.assign(state, initialState);
		},
	},
});

// Export actions
export const {
	setCodeFrom,
	setCodeTo,
	setShippingLine,
	setContainerType,
	setShippingType,
	setTransportType,
	setCargoType,
	setSelectedDate,
	setSelectedWeeks,
	setSelectedShippingLines,
	setAllCarrierScacs,
	setSearchOn,
	setOnlyDirectService,
	setShowAlternativeOptions,
	resetFilters,
} = geocodeFiltersSlice.actions;

// Export reducer
export default geocodeFiltersSlice.reducer;
